import React from 'react';
import { Upload, X } from 'lucide-react';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useLanguage } from '../../i18n';

const Step6_Contact = ({ 
  formData, 
  files,
  isDragging,
  showAddressPredictions,
  addressPredictions,
  showAddressConfirm,
  validatedAddress,
  addressValidation,
  addressCoordinates,
  loading,
  onInputChange,
  onFormDataChange,
  onFileChange,
  onRemoveFile,
  onDragEnter,
  onDragLeave,
  onDragOver,
  onDrop,
  onSearchAddress,
  onSelectAddress,
  onValidateAddress,
  onCloseAddressConfirm,
  onBack,
  onSubmit
}) => {
  const { t } = useLanguage();

  return (
    <div className="step-content" data-testid="step-6">
      <h2 className="step-title">{t('step6Title')}</h2>
      <p className="step-description">{t('step6Desc')}</p>
      <div className="form-group">
        <label className="form-label">{t('nameLabel')}</label>
        <input type="text" name="name" value={formData.name} onChange={onInputChange} className="form-input" placeholder={t('namePlaceholder')} data-testid="name-input" />
      </div>
      <div className="form-group">
        <label className="form-label">{t('emailLabel')}</label>
        <input type="email" name="email" value={formData.email} onChange={onInputChange} className="form-input" placeholder={t('emailPlaceholder')} data-testid="email-input" />
      </div>
      <div className="form-group">
        <label className="form-label">{t('phoneLabel')}</label>
        <input type="tel" name="phone" value={formData.phone} onChange={onInputChange} className="form-input" placeholder={t('phonePlaceholder')} data-testid="phone-input" />
      </div>
      <div className="form-group">
        <label className="form-label">{t('addressLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('addressOptional')})</span></label>
        <div style={{ position: 'relative' }}>
          <input type="text" name="address" value={formData.address} onChange={(e) => { onInputChange(e); onSearchAddress(e.target.value); }} className="form-input" placeholder={t('addressPlaceholder')} data-testid="address-input" />
          <p style={{ fontSize: '0.75rem', color: '#1976D2', marginTop: '0.5rem', fontStyle: 'italic' }}>
            {t('addressTip')}
          </p>
          {showAddressPredictions && addressPredictions.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 1000, maxHeight: '200px', overflowY: 'auto', marginTop: '0.25rem' }}>
              {addressPredictions.map((prediction, index) => (
                <div key={prediction.place_id} onClick={() => onSelectAddress(prediction)} style={{ padding: '0.75rem', cursor: 'pointer', borderBottom: index < addressPredictions.length - 1 ? '1px solid #f0f0f0' : 'none' }} onMouseEnter={(e) => e.target.style.background = '#f5f5f5'} onMouseLeave={(e) => e.target.style.background = 'white'}>
                  <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{prediction.structured_formatting?.main_text || prediction.description}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666' }}>{prediction.structured_formatting?.secondary_text || ''}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        {showAddressConfirm && (
          <div style={{ marginTop: '0.5rem', padding: '1rem', background: '#FFF3E0', borderRadius: '8px', border: '1px solid #F57C00' }}>
            <p style={{ fontSize: '0.875rem', color: '#E65100', marginBottom: '0.5rem', fontWeight: '600' }}>{t('addressCheckTitle')}</p>
            {validatedAddress && (
              <>
                <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>{t('addressDidYouMean')} <strong>{validatedAddress}</strong>?</p>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button type="button" onClick={() => { onFormDataChange({ ...formData, address: validatedAddress }); onCloseAddressConfirm(); }} className="btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>{t('addressYesAccept')}</button>
                  <button type="button" onClick={onCloseAddressConfirm} className="btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>{t('addressNoCorrect')}</button>
                </div>
              </>
            )}
            {addressValidation && !validatedAddress && (
              <>
                <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>{addressValidation}</p>
                <button type="button" onClick={onCloseAddressConfirm} className="btn-secondary" style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}>{t('addressOkCorrect')}</button>
              </>
            )}
          </div>
        )}
      </div>
      {formData.address && formData.address.trim().length >= 5 && !showAddressConfirm && (
        <div style={{ marginBottom: '1rem' }}>
          <button type="button" onClick={onValidateAddress} className="btn-secondary" style={{ fontSize: '0.875rem' }}>{t('addressCheck')}</button>
        </div>
      )}
      
      {formData.address && formData.address.length > 10 && (
        <div className="form-group">
          <label className="form-label">{t('addressOnMap')}</label>
          <div style={{ height: '250px', border: '1px solid #ddd', borderRadius: '8px', overflow: 'hidden' }}>
            <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
              <Map key={addressCoordinates ? `${addressCoordinates.lat}-${addressCoordinates.lng}` : 'default'} style={{ width: '100%', height: '100%' }} defaultCenter={addressCoordinates ? { lat: addressCoordinates.lat, lng: addressCoordinates.lng } : { lat: 47.0502, lng: 8.3093 }} defaultZoom={addressCoordinates ? 15 : 8} mapId="address-preview-map" gestureHandling="greedy" zoomControl={true} disableDefaultUI={false}>
                {addressCoordinates && <AdvancedMarker position={{ lat: addressCoordinates.lat, lng: addressCoordinates.lng }} />}
              </Map>
            </APIProvider>
          </div>
          <p style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.5rem' }}>
            {addressCoordinates ? `${t('addressMapCentered')} ${addressCoordinates.address}` : t('addressMapWaiting')}
          </p>
        </div>
      )}
      
      <div className="form-group">
        <label className="form-label">{t('installerQuestion')}</label>
        <div className="radio-group">
          <div className="radio-option">
            <input type="radio" name="has_installer" value="true" checked={formData.has_installer === true} onChange={() => onFormDataChange({ ...formData, has_installer: true })} id="installer-yes" data-testid="installer-yes" />
            <label htmlFor="installer-yes" className="radio-label">{t('yes')}</label>
          </div>
          <div className="radio-option">
            <input type="radio" name="has_installer" value="false" checked={formData.has_installer === false} onChange={() => onFormDataChange({ ...formData, has_installer: false, installer_info: '' })} id="installer-no" data-testid="installer-no" />
            <label htmlFor="installer-no" className="radio-label">{t('no')}</label>
          </div>
        </div>
      </div>
      
      {formData.has_installer === true && (
        <div className="form-group">
          <label className="form-label">{t('installerInfoLabel')}</label>
          <input type="text" name="installer_info" value={formData.installer_info} onChange={onInputChange} className="form-input" placeholder={t('installerInfoPlaceholder')} maxLength={200} data-testid="installer-info-input" />
        </div>
      )}
      
      {formData.has_installer === false && (
        <div style={{ padding: '0.75rem 1rem', background: '#E8F5E9', borderRadius: '8px', marginBottom: '1rem', color: '#2E7D32', fontSize: '0.9rem' }}>
          {t('noInstallerHint')}
        </div>
      )}
      
      <div className="form-group">
        <label className="form-label">{t('additionalInfoLabel')} <span style={{ fontSize: '0.875rem', color: '#666' }}>({t('optional')})</span> <span style={{ fontSize: '0.75rem', color: '#888', marginLeft: '0.5rem' }}>{formData.additional_info.length}/250</span></label>
        <textarea name="additional_info" value={formData.additional_info} onChange={onInputChange} className="form-input" rows="4" placeholder={t('additionalInfoPlaceholder')} maxLength={250} data-testid="additional-info-textarea" />
      </div>
      <div className="form-group">
        <label className="form-label">{t('fileUploadLabel')}</label>
        <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '0.5rem' }}>{t('fileUploadDesc')}</p>
        <input type="file" onChange={onFileChange} accept=".pdf,.jpg,.jpeg,.png" multiple style={{ display: 'none' }} id="file-upload" data-testid="file-input" />
        <label htmlFor="file-upload" className={`file-upload-zone ${isDragging ? 'drag-over' : ''}`} onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
          <Upload size={32} style={{ margin: '0 auto 0.5rem', display: 'block', color: '#1B5E20' }} />
          <p>{isDragging ? t('fileUploadDragging') : t('fileUploadDrag')}</p>
        </label>
        {files.length > 0 && (
          <div className="uploaded-files">
            {files.map((file, index) => (
              <div key={index} className="uploaded-file">
                <span>{file.name}</span>
                <button onClick={() => onRemoveFile(index)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#C62828' }}><X size={18} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="form-group">
        <div className="checkbox-group">
          <input type="checkbox" name="consent" checked={formData.consent} onChange={onInputChange} id="consent" data-testid="consent-checkbox" />
          <label htmlFor="consent">
            {t('consentText')} <a href="/datenschutz" target="_blank" style={{ color: '#1B5E20', textDecoration: 'underline' }}>{t('consentLink')}</a> {t('consentSuffix')}
          </label>
        </div>
      </div>
      <div className="button-group">
        <button onClick={onBack} className="btn-secondary" data-testid="step-6-back-btn">{t('back')}</button>
        <button onClick={onSubmit} className="btn-primary" disabled={!formData.name || !formData.email || !formData.phone || !formData.consent || loading} data-testid="submit-lead-btn">
          {loading ? t('sending') : t('submitBtn')}
        </button>
      </div>
      <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#888', marginTop: '0.75rem' }}>
        {t('submitHint')}
      </p>
    </div>
  );
};

export default Step6_Contact;
